import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { api } from '../../lib/api';
import { IconSpark } from '../icons';

type SurveyKind = 'single' | 'multi' | 'text' | 'longtext' | 'number' | 'range' | 'email';

interface SurveyQuestion {
  id: string;
  text: string;
  kind: SurveyKind;
  options: string[];
  placeholder?: string;
  min?: number;
  max?: number;
  step?: number;
  unit?: string;
  help?: string;
  allowCustom: boolean;
}

interface SurveyPayload {
  question: SurveyQuestion | null;
  total: number;
  answeredCount: number;
  answers?: Array<{ questionId: string; question: string; answer: string }>;
  done: boolean;
}

/**
 * The smart adaptive survey (spec 5b.7 + 35c + 35d), styled as a polished onboarding flow: a progress
 * bar, a big question, one tap-friendly input, and a full-width Continue — the shape non-coders already
 * recognise from every app's first-run screens. Every kind (single/multi/text/longtext/number/range/
 * email) has a purpose-built control, a custom answer is always reachable, Back re-shows the previous
 * question exactly, and Skip builds with whatever has been answered.
 *
 * Responsive: one column on phones; a two-pane layout on desktop (context on the left, the input on the
 * right) so the wide studio does not leave a lonely column of controls in the middle of the screen.
 */
export function SurveyPanel({
  projectId,
  onDone,
}: {
  projectId: string;
  onDone: () => void;
}) {
  const [state, setState] = useState<SurveyPayload | null>(null);
  const [explanation, setExplanation] = useState<string | null>(null);
  const [explaining, setExplaining] = useState(false);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  /** always the latest onDone, without making it a dependency of `load` */
  const onDoneRef = useRef(onDone);
  onDoneRef.current = onDone;

  const load = useCallback(async () => {
    try {
      const data = await api.get<SurveyPayload>(`/box/projects/${projectId}/survey`);
      setState(data);
      /*
       * The survey can arrive ALREADY done — when the generator produced no questions for this
       * idea, or the learner reloaded after finishing. Until now onDone() fired only from answer()
       * and skip(), so those projects sat on "Putting your first version together now…" forever and
       * the first build was never started. Found by walking a real build in the browser.
       * startFirstBuild() is guarded by its own `kickedOff` ref, so firing here is safe.
       */
      /*
       * Only auto-start when there is nothing to review — the generator produced no questions for
       * this idea. When there ARE answers, the learner gets to check them first: answering the last
       * question used to fire onDone() immediately, so the summary appeared for a single frame and
       * the build was already running before anyone could read it.
       */
      if (data.done && (data.answers?.length ?? 0) === 0) onDoneRef.current();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not load the questions');
    }
    /* onDone is read through a ref on purpose: the caller passes an inline arrow, so depending on
       it directly would give `load` a new identity every render and the effect below would loop. */
  }, [projectId]);

  useEffect(() => {
    void load();
  }, [load]);

  const answer = useCallback(
    async (value: string | string[]) => {
      if (!state?.question || busy) return;
      setBusy(true);
      setExplanation(null);
      try {
        const next = await api.post<SurveyPayload>(`/box/projects/${projectId}/survey/answer`, {
          questionId: state.question.id,
          answer: value,
        });
        setState(prev => ({ ...(prev ?? { answeredCount: 0, total: 0 }), ...next }));
        /*
         * The last answer ends the questions but NOT the survey — re-load so the review below has
         * the full answer list (the answer response carries progress only), then let the learner
         * confirm. onDone() now fires from that confirmation alone.
         */
        if (next.done) await load();
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Could not save that answer');
      } finally {
        setBusy(false);
      }
    },
    [state?.question, busy, projectId, load],
  );

  const goBack = useCallback(async () => {
    if (busy) return;
    setBusy(true);
    setExplanation(null);
    try {
      const prev = await api.post<SurveyPayload>(`/box/projects/${projectId}/survey/back`, {});
      setState(cur => ({ ...(cur ?? { total: 0 }), ...prev }));
    } catch {
      /* nothing to go back to */
    } finally {
      setBusy(false);
    }
  }, [busy, projectId]);

  /** Answers in the order they were given — the review list, and what `editFrom` indexes into. */
  const reviewAnswers = useMemo(() => state?.answers ?? [], [state?.answers]);

  /*
   * Re-open the answer at `index`. The survey is adaptive: every question after this one was chosen
   * BECAUSE of the current answer, so they are no longer valid and the server discards them as it
   * steps back. `/back` pops exactly one question per call, so walk it back that many times.
   */
  const editFrom = useCallback(
    async (index: number) => {
      if (busy) return;
      const steps = reviewAnswers.length - index;
      if (steps <= 0) return;
      setBusy(true);
      setExplanation(null);
      try {
        let latest: SurveyPayload | null = null;
        for (let i = 0; i < steps; i += 1) {
          latest = await api.post<SurveyPayload>(`/box/projects/${projectId}/survey/back`, {});
        }
        if (latest) setState(cur => ({ ...(cur ?? { total: 0 }), ...latest! }));
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Could not reopen that answer');
      } finally {
        setBusy(false);
      }
    },
    [busy, projectId, reviewAnswers.length],
  );

  const skip = useCallback(async () => {
    if (busy) return;
    setBusy(true);
    try {
      await api.post(`/box/projects/${projectId}/survey/finish`, {});
      onDone();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Could not skip');
      setBusy(false);
    }
  }, [busy, projectId, onDone]);

  const explain = async () => {
    if (!state?.question || explaining) return;
    setExplaining(true);
    setExplanation(null);
    try {
      const data = await api.post<{ explanation: string }>(`/box/projects/${projectId}/survey/explain`, {
        questionId: state.question.id,
      });
      setExplanation(data.explanation);
    } catch {
      setExplanation('Answer it however makes sense to you — you can change it later.');
    } finally {
      setExplaining(false);
    }
  };

  if (error) {
    return <p className="text-sm text-muted">{error}</p>;
  }
  if (!state) {
    return (
      <div className="w-full max-w-[880px] space-y-3">
        <div className="skeleton h-2 w-full rounded-full" />
        <div className="skeleton h-9 w-2/3 rounded-field" />
        <div className="skeleton h-40 w-full rounded-card" />
      </div>
    );
  }

  /*
   * REVIEW — the last answer ends the questions, and this is where the learner checks them before a
   * single spark is spent. Everything below is what the build prompt is composed from, so a typo or
   * a mis-tapped option is much cheaper to catch here than after a two-minute build.
   */
  if (state.done && reviewAnswers.length > 0) {
    return (
      <div className="anim-in w-full max-w-[720px]">
        <div className="text-center">
          <span className="mx-auto grid h-12 w-12 place-items-center rounded-2xl bg-accent/15 text-accent">
            <IconSpark width={24} height={24} />
          </span>
          <h1 className="mt-4 text-2xl font-extrabold tracking-tight">Does this look right?</h1>
          <p className="mt-1 text-sm text-muted">
            This is what I will build from. Change anything that is not quite right.
          </p>
        </div>

        <ol className="mt-6 space-y-2">
          {reviewAnswers.map((entry, index) => (
            <li
              key={entry.questionId}
              className="flex items-start gap-3 rounded-card border border-line bg-surface px-4 py-3"
            >
              <span className="min-w-0 flex-1">
                <span className="block text-xs font-semibold text-muted">{entry.question}</span>
                <span className="mt-0.5 block text-sm font-semibold">{entry.answer}</span>
              </span>
              <button
                type="button"
                onClick={() => void editFrom(index)}
                disabled={busy}
                className="shrink-0 rounded-lg border border-line px-2.5 py-1 text-xs font-semibold text-muted transition hover:text-ink disabled:opacity-60"
              >
                Change
              </button>
            </li>
          ))}
        </ol>

        {/* Changing an answer re-asks everything after it: later questions were CHOSEN based on it. */}
        <p className="mt-3 text-center text-xs text-muted">
          Changing an answer will ask the questions after it again.
        </p>

        <button
          type="button"
          onClick={onDone}
          disabled={busy}
          className="mt-5 w-full rounded-field bg-accent px-4 py-3 text-sm font-bold text-accent-ink transition hover:brightness-110 disabled:opacity-60"
        >
          {busy ? 'One moment…' : 'Build it'}
        </button>
      </div>
    );
  }

  if (state.done || !state.question) {
    return (
      <div className="anim-in text-center">
        <span className="mx-auto grid h-12 w-12 place-items-center rounded-2xl bg-accent/15 text-accent">
          <IconSpark width={24} height={24} />
        </span>
        <h1 className="mt-4 text-2xl font-extrabold tracking-tight">That is everything I need</h1>
        <p className="mt-1 text-sm text-muted">Putting your first version together now…</p>
      </div>
    );
  }

  const { question } = state;
  const pct = state.total > 0 ? Math.min(100, Math.round((state.answeredCount / state.total) * 100)) : 0;
  const canGoBack = state.answeredCount > 0;

  const explainButton = (visibilityCls: string) => (
    <button
      type="button"
      onClick={() => void explain()}
      disabled={explaining}
      className={`mt-4 inline-flex items-center gap-1.5 text-xs font-semibold text-accent underline-offset-2 hover:underline disabled:no-underline disabled:opacity-70 ${visibilityCls}`}
    >
      {explaining && <Spinner />}
      {explaining ? 'Asking Sparky…' : "I don't understand this question"}
    </button>
  );

  return (
    <div className="anim-in w-full max-w-[880px]">
      {/* progress + top controls */}
        <div className="mb-6">
          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => void goBack()}
              disabled={!canGoBack || busy}
              aria-label="Back"
              className="grid h-9 w-9 shrink-0 place-items-center rounded-full border border-line text-muted transition hover:text-ink disabled:pointer-events-none disabled:opacity-40"
            >
              <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <path d="M15 18l-6-6 6-6" />
              </svg>
            </button>
            <div className="h-2 flex-1 overflow-hidden rounded-full bg-page">
              <div className="h-full rounded-full bg-accent transition-all duration-500" style={{ width: `${Math.max(pct, 6)}%` }} />
            </div>
            <span className="shrink-0 text-xs font-semibold tabular-nums text-muted">
              {state.answeredCount + 1}/{state.total}
            </span>
          </div>
        </div>

        {/* two-pane on desktop: context left, input right */}
        <div className="lg:grid lg:grid-cols-[minmax(0,1fr)_minmax(0,1.15fr)] lg:gap-10">
          <div className="lg:pt-2">
            <p className="text-[11px] font-bold uppercase tracking-wider text-accent-deep">A few quick questions</p>
            <h1 className="mt-2 text-[26px] font-extrabold leading-tight tracking-tight sm:text-[30px]">
              {question.text}
            </h1>
            {question.help && <p className="mt-2 text-sm text-muted">{question.help}</p>}

            {(explaining || explanation) && (
              <div className="mt-4 rounded-field border border-line bg-page p-3">
                <div className="mb-1 flex items-center gap-2 text-xs font-bold">
                  <span className="text-accent">
                    <IconSpark width={14} height={14} />
                  </span>
                  Sparky
                </div>
                {explaining ? (
                  <div className="space-y-1.5 py-0.5">
                    <div className="skeleton h-3 w-full rounded-full" />
                    <div className="skeleton h-3 w-4/5 rounded-full" />
                  </div>
                ) : (
                  <p className="text-sm text-muted">{explanation}</p>
                )}
              </div>
            )}

            {explainButton('hidden lg:inline-flex')}
          </div>

          <div className="mt-6 lg:mt-0">
            <QuestionInput key={question.id} question={question} busy={busy} onSubmit={answer} />

            {explainButton('lg:hidden')}

            <div className="mt-6 text-center">
              <button
                type="button"
                onClick={() => void skip()}
                disabled={busy}
                className="text-xs font-semibold text-muted underline underline-offset-2 transition hover:text-ink disabled:opacity-50"
              >
                I&apos;ll explore later
              </button>
            </div>
          </div>
        </div>
      </div>
  );
}

/* ------------------------------------- input controls -------------------------------------- */

/** Small inline spinner for in-place loading (no emojis — inline SVG per the UI rules). */
function Spinner() {
  return (
    <svg className="animate-spin" width="12" height="12" viewBox="0 0 24 24" fill="none" aria-hidden>
      <circle cx="12" cy="12" r="9" stroke="currentColor" strokeWidth="3" opacity="0.25" />
      <path d="M21 12a9 9 0 0 0-9-9" stroke="currentColor" strokeWidth="3" strokeLinecap="round" />
    </svg>
  );
}

const continueCls =
  'inline-flex h-12 w-full items-center justify-center rounded-2xl bg-ink px-6 text-sm font-bold text-surface transition hover:brightness-110 disabled:pointer-events-none disabled:opacity-40';
const fieldCls =
  'w-full rounded-field border border-line bg-surface px-4 py-3 text-sm outline-none ring-1 ring-transparent transition placeholder:text-muted focus:border-ink/50 focus:ring-ink/20';

/** Renders the right control for each question kind, ending in a Continue button. */
function QuestionInput({
  question,
  busy,
  onSubmit,
}: {
  question: SurveyQuestion;
  busy: boolean;
  onSubmit: (value: string | string[]) => void;
}) {
  const { kind } = question;
  if (kind === 'single' || kind === 'multi') return <ChoiceInput question={question} busy={busy} onSubmit={onSubmit} />;
  if (kind === 'number' || kind === 'range') return <NumberInput question={question} busy={busy} onSubmit={onSubmit} />;
  return <FreeEntry question={question} busy={busy} onSubmit={onSubmit} />;
}

/** single + multi choice: a 2-column card grid for a short list, chips when it overflows. */
function ChoiceInput({
  question,
  busy,
  onSubmit,
}: {
  question: SurveyQuestion;
  busy: boolean;
  onSubmit: (value: string[]) => void;
}) {
  const multi = question.kind === 'multi';
  const [picked, setPicked] = useState<string[]>([]);
  const [custom, setCustom] = useState('');
  const asCards = question.options.length > 0 && question.options.length <= 6;

  const toggle = (option: string) =>
    setPicked(prev => {
      if (multi) return prev.includes(option) ? prev.filter(o => o !== option) : [...prev, option];
      return prev.includes(option) ? [] : [option];
    });

  const values = useMemo(() => {
    const all = [...picked];
    if (custom.trim()) all.push(custom.trim());
    return all;
  }, [picked, custom]);

  return (
    <div className="space-y-4">
      {multi && <p className="text-xs font-semibold text-muted">You can choose more than one.</p>}

      {asCards ? (
        <div className="grid grid-cols-2 gap-2.5">
          {question.options.map(option => {
            const on = picked.includes(option);
            return (
              <button
                key={option}
                type="button"
                onClick={() => toggle(option)}
                disabled={busy}
                aria-pressed={on}
                className={[
                  'relative min-h-[76px] rounded-2xl border p-3.5 text-left text-sm font-semibold transition disabled:opacity-50',
                  on
                    ? 'border-accent bg-accent/10 text-ink shadow-sm'
                    : 'border-line bg-surface hover:-translate-y-0.5 hover:border-accent/60',
                ].join(' ')}
              >
                {on && (
                  <span className="absolute right-2.5 top-2.5 grid h-4 w-4 place-items-center rounded-full bg-accent text-accent-ink">
                    <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="4" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M20 6L9 17l-5-5" />
                    </svg>
                  </span>
                )}
                {option}
              </button>
            );
          })}
        </div>
      ) : (
        <div className="flex flex-wrap gap-2">
          {question.options.map(option => {
            const on = picked.includes(option);
            return (
              <button
                key={option}
                type="button"
                onClick={() => toggle(option)}
                disabled={busy}
                aria-pressed={on}
                className={[
                  'inline-flex items-center gap-1.5 rounded-full border px-3.5 py-2 text-sm font-semibold transition disabled:opacity-50',
                  on ? 'border-accent bg-accent text-accent-ink' : 'border-line hover:border-accent hover:bg-accent/10',
                ].join(' ')}
              >
                {on && (
                  <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M20 6L9 17l-5-5" />
                  </svg>
                )}
                {option}
              </button>
            );
          })}
        </div>
      )}

      <input
        value={custom}
        onChange={e => setCustom(e.target.value)}
        placeholder={question.options.length ? 'Or add your own' : 'Type your answer'}
        className={fieldCls}
      />

      <button type="button" onClick={() => values.length && onSubmit(values)} disabled={busy || values.length === 0} className={continueCls}>
        Continue{multi && values.length > 0 ? ` (${values.length})` : ''}
      </button>
    </div>
  );
}

/** number / range: a big live value with a slider and steppers — the "pick a number" onboarding feel. */
function NumberInput({
  question,
  busy,
  onSubmit,
}: {
  question: SurveyQuestion;
  busy: boolean;
  onSubmit: (value: string) => void;
}) {
  const min = question.min ?? 0;
  const max = question.max ?? min + 10;
  const step = question.step ?? 1;
  const [value, setValue] = useState<number>(() => Math.round((min + max) / 2 / step) * step);
  const [custom, setCustom] = useState('');
  const clamp = (n: number) => Math.min(max, Math.max(min, n));
  // a typed answer wins over the picker — the learner might want "unlimited" or "3, +1 each level"
  const usingCustom = custom.trim().length > 0;
  const submit = () =>
    onSubmit(usingCustom ? custom.trim() : question.unit ? `${value} ${question.unit}` : String(value));

  return (
    <div className="space-y-4">
      <div className={`flex items-center justify-center gap-4 rounded-2xl border py-6 transition ${usingCustom ? 'border-line bg-page/50 opacity-40' : 'border-line bg-page'}`}>
        <button
          type="button"
          onClick={() => setValue(v => clamp(v - step))}
          disabled={busy || value <= min || usingCustom}
          className="grid h-10 w-10 place-items-center rounded-full border border-line text-lg font-bold text-muted transition hover:text-ink disabled:opacity-40"
        >
          −
        </button>
        <div className="min-w-[96px] text-center">
          <span className="text-4xl font-black tabular-nums text-ink">{value}</span>
          {question.unit && <span className="mt-1 block text-xs font-semibold text-muted">{question.unit}</span>}
        </div>
        <button
          type="button"
          onClick={() => setValue(v => clamp(v + step))}
          disabled={busy || value >= max || usingCustom}
          className="grid h-10 w-10 place-items-center rounded-full border border-line text-lg font-bold text-muted transition hover:text-ink disabled:opacity-40"
        >
          +
        </button>
      </div>
      <input
        type="range"
        min={min}
        max={max}
        step={step}
        value={value}
        onChange={e => setValue(Number(e.target.value))}
        disabled={usingCustom}
        className="w-full accent-accent disabled:opacity-40"
      />
      <input
        value={custom}
        onChange={e => setCustom(e.target.value)}
        onKeyDown={e => e.key === 'Enter' && !busy && submit()}
        placeholder="Or answer in your own words"
        className={fieldCls}
      />
      <button type="button" onClick={submit} disabled={busy} className={continueCls}>
        Continue
      </button>
    </div>
  );
}

/** text / longtext / email — the free-entry kinds. longtext shows a worked example placeholder. */
function FreeEntry({
  question,
  busy,
  onSubmit,
}: {
  question: SurveyQuestion;
  busy: boolean;
  onSubmit: (value: string) => void;
}) {
  const { kind } = question;
  const [value, setValue] = useState('');
  const [touched, setTouched] = useState(false);
  const emailOk = kind !== 'email' || /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value.trim());
  const ready = value.trim().length > 0 && emailOk;
  const placeholder = question.placeholder ?? (kind === 'email' ? 'name@example.com' : 'Type your answer');

  return (
    <div className="space-y-3">
      {kind === 'longtext' ? (
        <div className="rounded-2xl border border-line bg-surface p-1 focus-within:border-ink/40">
          {!value && question.placeholder && (
            <p className="px-3 pt-3 text-[11px] font-bold uppercase tracking-wider text-muted">Example</p>
          )}
          <textarea
            value={value}
            onChange={e => setValue(e.target.value)}
            rows={5}
            placeholder={placeholder}
            className="w-full resize-y bg-transparent px-3 py-2 text-sm outline-none placeholder:text-muted"
          />
        </div>
      ) : (
        <input
          type={kind === 'email' ? 'email' : 'text'}
          inputMode={kind === 'email' ? 'email' : 'text'}
          value={value}
          onChange={e => setValue(e.target.value)}
          onBlur={() => setTouched(true)}
          onKeyDown={e => e.key === 'Enter' && ready && onSubmit(value.trim())}
          placeholder={placeholder}
          className={fieldCls}
        />
      )}
      {kind === 'email' && touched && value.trim() && !emailOk && (
        <p className="text-xs text-danger">That does not look like an email address.</p>
      )}
      <button type="button" onClick={() => ready && onSubmit(value.trim())} disabled={busy || !ready} className={continueCls}>
        Continue
      </button>
    </div>
  );
}
